import React, { useMemo, useCallback, useRef, useEffect, useState } from "react";
import { OverlayView, OverlayViewF } from "@react-google-maps/api";
import { dtGreen } from "../../../../shared/themes/redesignTokens";

// ===================================================================
//  TYPES
// ===================================================================

interface TechnicianMarkerProps {
  lat: number;
  lng: number;
  heading: number | null;
  prevLat?: number | null;
  prevLng?: number | null;
  initials: string;
  avatarUrl?: string | null;
  isStationary?: boolean; // stationary > 5 min
}

// ===================================================================
//  CONSTANTS
// ===================================================================

const MARKER_SIZE = 48;
const ANIMATION_DURATION_MS = 800; // Smooth transition between GPS points

// ===================================================================
//  HEADING CALCULATION
// ===================================================================

/**
 * Calculate bearing (heading) from previous position to current position.
 * Returns degrees 0-360 (0=north, 90=east, 180=south, 270=west).
 */
const calculateHeading = (
  prevLat: number,
  prevLng: number,
  curLat: number,
  curLng: number
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const dLng = toRad(curLng - prevLng);
  const lat1 = toRad(prevLat);
  const lat2 = toRad(curLat);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
};

// ===================================================================
//  COMPONENT
// ===================================================================

const TechnicianMarker: React.FC<TechnicianMarkerProps> = ({
  lat,
  lng,
  heading: headingProp,
  prevLat,
  prevLng,
  initials,
  avatarUrl,
  isStationary = false,
}) => {
  // --- Smooth position interpolation ---
  const animRef = useRef<number | null>(null);
  const [animLat, setAnimLat] = useState(lat);
  const [animLng, setAnimLng] = useState(lng);
  const prevPosRef = useRef({ lat, lng });

  useEffect(() => {
    // Cancel any running animation
    if (animRef.current != null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }

    const startLat = prevPosRef.current.lat;
    const startLng = prevPosRef.current.lng;
    const startTime = performance.now();

    // If distance is trivially small, skip animation
    const dLat = Math.abs(lat - startLat);
    const dLng = Math.abs(lng - startLng);
    if (dLat < 0.000001 && dLng < 0.000001) {
      prevPosRef.current = { lat, lng };
      return;
    }

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      const newLat = startLat + (lat - startLat) * eased;
      const newLng = startLng + (lng - startLng) * eased;

      setAnimLat(newLat);
      setAnimLng(newLng);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        prevPosRef.current = { lat, lng };
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current != null) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [lat, lng]);

  // --- Direction arrow: calculate heading from previous to current position ---
  const computedHeading = useMemo(() => {
    // Prefer explicit heading from GPS
    if (headingProp != null && headingProp !== 0) return headingProp;
    // Calculate from previous position
    if (prevLat != null && prevLng != null) {
      const dLat = Math.abs(lat - prevLat);
      const dLng = Math.abs(lng - prevLng);
      // Only calculate if moved enough (avoid jitter)
      if (dLat > 0.0001 || dLng > 0.0001) {
        return calculateHeading(prevLat, prevLng, lat, lng);
      }
    }
    return null;
  }, [headingProp, prevLat, prevLng, lat, lng]);

  const position = useMemo(() => ({ lat: animLat, lng: animLng }), [animLat, animLng]);

  const getPixelPositionOffset = useCallback(
    () => ({ x: -(MARKER_SIZE / 2), y: -(MARKER_SIZE / 2) }),
    []
  );

  const pulseColor = isStationary ? dtGreen.amber500 : dtGreen.green500;

  return (
    <OverlayViewF
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        style={{
          position: "relative",
          width: MARKER_SIZE,
          height: MARKER_SIZE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Pulsing ring — green or amber when stationary */}
        <div
          style={{
            position: "absolute",
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            borderRadius: "50%",
            border: `2.5px solid ${pulseColor}`,
            animation: isStationary
              ? "technicianAmberPulse 1.5s ease-in-out infinite"
              : "technicianPulse 2s ease-in-out infinite",
          }}
        />

        {/* Second outer pulse ring (slower, wider) */}
        <div
          style={{
            position: "absolute",
            width: MARKER_SIZE + 12,
            height: MARKER_SIZE + 12,
            top: -6,
            left: -6,
            borderRadius: "50%",
            border: `1.5px solid ${pulseColor}`,
            opacity: 0.4,
            animation: isStationary
              ? "technicianAmberPulseOuter 1.8s ease-in-out infinite"
              : "technicianPulseOuter 2.5s ease-in-out infinite",
          }}
        />

        {/* Main circle */}
        <div
          style={{
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            borderRadius: "50%",
            background: isStationary
              ? `linear-gradient(135deg, ${dtGreen.amber500}, #D97706)`
              : `linear-gradient(135deg, ${dtGreen.green500}, ${dtGreen.green700})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 2px 8px rgba(0,0,0,0.25), 0 0 0 3px rgba(255,255,255,0.8)`,
            position: "relative",
            overflow: "hidden",
            transition: "background 0.6s ease",
          }}
        >
          {/* Avatar or initials */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              style={{
                width: MARKER_SIZE - 6,
                height: MARKER_SIZE - 6,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: dtGreen.font,
                fontSize: "16px",
                fontWeight: 700,
                color: "#FFFFFF",
                userSelect: "none",
                letterSpacing: "0.5px",
              }}
            >
              {initials}
            </span>
          )}
        </div>

        {/* Direction arrow — rotated to heading */}
        {computedHeading !== null && !isStationary && (
          <div
            style={{
              position: "absolute",
              top: -10,
              left: "50%",
              transform: `translateX(-50%) rotate(${computedHeading}deg)`,
              transformOrigin: "center 34px",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderBottom: `10px solid ${dtGreen.green500}`,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
              transition: "transform 0.6s ease-out",
            }}
          />
        )}

        {/* "LIVE" label below */}
        <div
          style={{
            position: "absolute",
            bottom: -16,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: dtGreen.fontMono,
            fontSize: "9px",
            fontWeight: 700,
            color: isStationary ? dtGreen.amber500 : dtGreen.green600,
            whiteSpace: "nowrap",
            letterSpacing: "1.5px",
            textShadow: "0 1px 3px rgba(255,255,255,0.9)",
            userSelect: "none",
            transition: "color 0.6s ease",
          }}
        >
          LIVE
        </div>
      </div>
    </OverlayViewF>
  );
};

export default React.memo(TechnicianMarker);
